import { useEffect, useState } from 'react';
import { getPracticeInfoURL, getPracticeTypeRefURL, getQueryData, getStateRefURL, postPracticeInfoURL, updateOnboardingStatusURL, updatePracticeInfoURL, withCredentials,instance as axiosInstance } from '../../../services';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress } from '@mui/material';
import { BackdropComponent, Controls } from '../../../components';
import { useForm } from 'react-hook-form';
import { practiceInfoFormDefaults, infoFormSchemaResolver, modifyStateList } from './practiceInfo.helper';
import { InformationCardHOC } from '../components';
import OnboardGridLayout from '../components/OnboardGridLayout';
import AutoSearch from '../../../components/controls/autoSearch/AutoSearch';
import Grid from '@mui/material/Grid2'
import TranslationMessage from './transMessage';
import { ErrorComponent } from './ErrorComponent';
import { modifyCommonDropDownData } from '../../common.helper';
import { useAppContext, useSnackbar } from '../../../contexts';
import { useNavigate } from 'react-router-dom';
import { validateErrorMsg } from '../../../utils/error.utils';

export const PracticeInformation = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const { providerId, userData, fetchProvider } = useAppContext()

    const [practiceTypeList, setPracticeTypeList] = useState<any>([])
    const [stateList, setStateList] = useState<any[]>([])
    const [practiceId, setPracticeId] = useState()
    const [error, setError] = useState("")
    const [formLoader, setFormLoader] = useState(false)

    const {
        handleSubmit,
        reset: formReset,
        control,
        formState: { errors },
        watch
    } = useForm<any>({
        resolver: infoFormSchemaResolver(t),
        defaultValues: practiceInfoFormDefaults
    });

    const selectedPracticeType = watch("practice_type");

    const { data: practiceTypeData, isLoading: practiceTypeLoading } = useQuery("practiceTypeData", async () =>
        getQueryData(getPracticeTypeRefURL, withCredentials())
    );

    const { data: stateListData, isLoading: stateRefLoading } = useQuery("stateListData", async () =>
        getQueryData(getStateRefURL, withCredentials())
    );

    const { data: practiceInfoData } = useQuery("practiceInfoData", async () =>
        getQueryData(getPracticeInfoURL(providerId), withCredentials()),
        { enabled: !!providerId }
    );

    useEffect(() => {
        if (practiceTypeData?.data) {
            const formatedPracticeTypes = modifyCommonDropDownData(practiceTypeData?.data)
            setPracticeTypeList(formatedPracticeTypes)
        }
    }, [practiceTypeData])

    useEffect(() => {
        if (stateListData?.data) {
            const formatedStateList = modifyStateList(stateListData?.data)
            setStateList(formatedStateList)
        }
    }, [stateListData])

    useEffect(() => {
        const formValues = { ...practiceInfoFormDefaults };

        if (Array.isArray(practiceInfoData?.data) && practiceInfoData?.data?.length > 0) {
            const prefillPracticeData = practiceInfoData?.data[0]
            formValues.organization_name = prefillPracticeData?.orgName
            formValues.practice_type = prefillPracticeData?.type
            formValues.organization_address_1 = prefillPracticeData?.address?.street1
            formValues.organization_address_2 = prefillPracticeData?.address?.street2
            formValues.city = prefillPracticeData?.address?.city
            formValues.state = prefillPracticeData?.address?.state
            formValues.zip = prefillPracticeData?.address?.zip
            formValues.practice_type_specify = prefillPracticeData?.otherTypeDetail
            formReset(formValues)
            setPracticeId(prefillPracticeData.practiceId)
        }
    }, [practiceInfoData, formReset])

    const onboardingStatusMutation = useMutation((params: any) => {
        setFormLoader(true)
        return axiosInstance().put(updateOnboardingStatusURL(providerId), params, withCredentials())
    },
        {
            onSuccess: () => {
                if (userData?.isNpiVerified) {
                    fetchProvider()
                    navigate("/home")
                }
                else if (!userData?.isNpiVerified && practiceId) {
                    setError("logout")
                }
                else {
                    setError("validation")
                }
            },
            onError: (error: any) => {
                showSnackbar(validateErrorMsg(error?.data?.detail),"error")
            },
            onSettled: () => {
                setFormLoader(false)
            }
        }
    );

    const submitPracticeInfoMutation = useMutation((params: any) => {
        setFormLoader(true)
        if (practiceId) {
            return axiosInstance().put(updatePracticeInfoURL(providerId, practiceId), params, withCredentials())
        }
        else {
            return axiosInstance().post(postPracticeInfoURL(providerId), params, withCredentials())
        }
    },
        {
            onSuccess: (res) => {
                const onboardParams = {
                    ...userData,
                    appOnBoardings: userData?.appOnBoardings.map((step: any) =>
                        step.type === "PracticeInformation" ? { ...step, isDone: true } : step
                    )
                }
                onboardingStatusMutation.mutate(onboardParams)
            },
            onError: (error: any) => {
                showSnackbar(validateErrorMsg(error?.data?.detail),"error")
            },
            onSettled: () => {
                setFormLoader(false)
            }
        }
    );

    const submitData = (formValues: any) => {
        if (userData) {
            const practiceInfoParams = {
                "orgName": formValues.organization_name,
                "address": {
                    "street1": formValues.organization_address_1,
                    "street2": formValues.organization_address_2,
                    "city": formValues.city,
                    "state": formValues.state,
                    "zip": formValues.zip,
                    "type": "SERVICE",
                    "latitude": 34.0522,
                    "longitude": -118.2437
                },
                "npi": userData?.npi,
                "type": formValues.practice_type,
                 "otherTypeDetail": formValues.practice_type_specify
            }
            submitPracticeInfoMutation.mutate(practiceInfoParams)
        }
        else {
            showSnackbar("Something went wrong!","error")
        }
    }

    return (
        <>
            <OnboardGridLayout>
                {!error ?
                    <InformationCardHOC
                        infoTitle={t(TranslationMessage.PRACTICE_INFO_TITLE)}
                        buttonText={t(TranslationMessage.SUBMIT_BUTTON_LABEL)}
                        onClickBtn={handleSubmit(submitData)}
                        buttonSx={{ width: "10.375rem" }}
                        rightBtnElement={formLoader ? <CircularProgress size={18} sx={{ color: "#FFF9C4", ml: "10px" }} /> : null}>
                        <Box component={'form'} sx={{ mt: "1rem" }}>
                            <Controls.Input
                                name="organization_name"
                                label={t(TranslationMessage.ORGANIZATION_LABEL)}
                                defaultValue={''}
                                control={control}
                                required
                                sx={{ mt: '0px' }}
                                error={errors.organization_name || ''}
                            />
                            <AutoSearch
                                control={control}
                                name='practice_type'
                                label={t(TranslationMessage.PRACTICE_TYPE_LABEL)}
                                options={practiceTypeList}
                                defaultValue={''}
                                sx={{ mt: '12px' }}
                                error={errors.practice_type || ''}
                            />

                            {selectedPracticeType === "Other" ?
                                <Controls.Input
                                    name="practice_type_specify"
                                    label={t(TranslationMessage.PRACTICE_TYPE_SPECIFY_LABEL)}
                                    defaultValue={''}
                                    control={control}
                                    required
                                    error={errors.practice_type_specify || ''}
                                />
                                : null}

                            <Controls.Input
                                name="organization_address_1"
                                label={t(TranslationMessage.ORGANIZATION_ADDRESS_LINE_1)}
                                defaultValue={''}
                                control={control}
                                required
                                error={errors.organization_address_1 || ''}
                            />

                            <Controls.Input
                                name="organization_address_2"
                                label={t(TranslationMessage.ORGANIZATION_ADDRESS_LINE_2)}
                                defaultValue={''}
                                control={control}
                                error={errors.organization_address_2 || ''}
                            />
                            <Grid container gap={'10px'} sx={{ mt: '12px' }} flex={1}>
                                <Grid flex={0.4}>
                                    <Controls.Input
                                        name="city"
                                        label={t(TranslationMessage.CITY_LABEL)}
                                        defaultValue={''}
                                        control={control}
                                        required
                                        sx={{ mt: '0px' }}
                                        error={errors.city || ''}
                                    />
                                </Grid>
                                <Grid flex={0.2}>
                                    <AutoSearch
                                        control={control}
                                        name='state'
                                        label={t(TranslationMessage.STATE_LABEL)}
                                        options={stateList}
                                        defaultValue={''}
                                        error={errors.state || ''}
                                        required
                                    />
                                </Grid>
                                <Grid flex={0.4}>
                                    <Controls.Input
                                        name="zip"
                                        label={t(TranslationMessage.ZIP_LABEL)}
                                        defaultValue={''}
                                        required
                                        sx={{ mt: '0px' }}
                                        control={control}
                                        error={errors.zip || ''}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </InformationCardHOC>
                    :
                    <ErrorComponent errorType={error} />
                }
            </OnboardGridLayout>
            <BackdropComponent showBackdrop={practiceTypeLoading || stateRefLoading} />
        </>
    );
}